import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, Trash2, Loader2, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Review {
  id: string;
  user_id: string;
  booking_id: string | null;
  rating: number;
  comment: string;
  submitted_at: string;
}

interface Photo {
  id: string;
  review_id: string;
  file_path: string;
  signedUrl?: string;
}

const AdminReviews = () => {
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [photos, setPhotos] = useState<Record<string, Photo[]>>({});
  const [profiles, setProfiles] = useState<Record<string, { full_name: string | null; email: string | null }>>({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data: revs, error } = await supabase
      .from("reviews")
      .select("*")
      .order("submitted_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setLoading(false);
      return;
    }
    setReviews(revs || []);

    const userIds = [...new Set((revs || []).map((r) => r.user_id))];
    if (userIds.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, full_name, email")
        .in("user_id", userIds);
      const map: Record<string, any> = {};
      (profs || []).forEach((p) => {
        map[p.user_id] = { full_name: p.full_name, email: p.email };
      });
      setProfiles(map);
    }

    // Load photos
    const reviewIds = (revs || []).map((r) => r.id);
    if (reviewIds.length) {
      const { data: pics } = await supabase
        .from("review_photos")
        .select("*")
        .in("review_id", reviewIds);
      const grouped: Record<string, Photo[]> = {};
      for (const p of pics || []) {
        const { data: signed } = await supabase.storage
          .from("review-photos")
          .createSignedUrl(p.file_path, 3600);
        const photo: Photo = { ...p, signedUrl: signed?.signedUrl };
        (grouped[p.review_id] ||= []).push(photo);
      }
      setPhotos(grouped);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const deleteReview = async (id: string) => {
    if (!confirm("Delete this review and its photos?")) return;
    const reviewPhotos = photos[id] || [];
    for (const p of reviewPhotos) {
      await supabase.storage.from("review-photos").remove([p.file_path]);
    }
    const { error } = await supabase.from("reviews").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Deleted" });
    load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl sm:text-3xl font-bold italic">Reviews</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {reviews.length} {reviews.length === 1 ? "review" : "reviews"} from clients
        </p>
      </div>

      {reviews.length === 0 ? (
        <Card className="bg-card border-border/50">
          <CardContent className="py-16 text-center text-muted-foreground">
            <MessageSquare size={32} className="mx-auto mb-3 opacity-50" />
            <p>No reviews yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {reviews.map((r) => {
            const profile = profiles[r.user_id];
            const reviewPhotos = photos[r.id] || [];
            return (
              <Card key={r.id} className="bg-card border-border/50">
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base font-serif italic">
                      {profile?.full_name || "Anonymous client"}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {profile?.email || "—"} · {new Date(r.submitted_at).toLocaleString()}
                    </p>
                    <div className="flex gap-0.5 mt-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          size={16}
                          className={
                            n <= r.rating
                              ? "fill-primary text-primary"
                              : "text-muted-foreground/30"
                          }
                        />
                      ))}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteReview(r.id)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 size={14} />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {r.comment && (
                    <p className="text-sm text-foreground/90 font-light leading-relaxed whitespace-pre-wrap">
                      "{r.comment}"
                    </p>
                  )}
                  {reviewPhotos.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {reviewPhotos.map((p) => (
                        <a
                          key={p.id}
                          href={p.signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-24 h-24 border border-border/50 overflow-hidden hover:opacity-80"
                        >
                          {p.signedUrl && (
                            <img src={p.signedUrl} alt="Review" className="w-full h-full object-cover" />
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
